import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Menu, X, MessageCircle, Phone, Mail, ChevronDown, ArrowUpRight } from "lucide-react";
import { getCategories, COMPANY, whatsappLink } from "@/lib/api";
import { Newsletter } from "@/components/shared";

const NAV = [
  { to: "/about", label: "About Us" },
  { to: "/products", label: "Products", mega: true },
  { to: "/industries", label: "Services" },
  { to: "/global-shipping", label: "How We Work" },
  { to: "/contact", label: "Contact" },
];

function Header() {
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(false);
  const [mega, setMega] = useState(false);
  const [solid, setSolid] = useState(false);
  const loc = useLocation();

  useEffect(() => { getCategories().then(setCats).catch(() => {}); }, []);
  useEffect(() => { setOpen(false); setMega(false); }, [loc.pathname]);
  useEffect(() => {
    const onScroll = () => setSolid(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${solid ? "bg-[hsl(var(--navy))]/95 backdrop-blur-md border-b border-white/10" : "bg-transparent"}`}
    >
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 flex items-center justify-between h-[72px]">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2 text-[hsl(var(--paper))]">
          <span className="font-display text-2xl tracking-tight">Ingren</span>
          <span className="font-mono-l text-[10px] text-[hsl(var(--brass-2))] uppercase tracking-[0.2em] mt-1">Trading LLP</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {NAV.map((n) => (
            <div key={n.to} onMouseEnter={() => setMega(n.mega)} onMouseLeave={() => setMega(false)} className="relative">
              <NavLink
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-sm text-[hsl(var(--paper))]/85 hover:text-[hsl(var(--brass-2))] transition-colors flex items-center gap-1 ${isActive ? "text-[hsl(var(--brass-2))]" : ""}`}
              >
                {n.label}{n.mega && <ChevronDown size={13} />}
              </NavLink>
            </div>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link to="/portal" data-testid="header-portal" className="hidden md:inline-flex items-center gap-1.5 text-white/80 hover:text-[hsl(var(--brass-2))] text-sm font-semibold transition-colors">
            Buyer Portal
          </Link>
          <Link to="/contact" data-testid="header-cta" className="hidden sm:inline-flex items-center gap-1.5 bg-[hsl(var(--brass))] text-[hsl(var(--ink))] px-4 py-2 text-sm font-semibold hover:bg-[hsl(var(--brass-2))] transition-colors">
            Request Quote <ArrowUpRight size={15} />
          </Link>
          <button data-testid="mobile-menu-btn" className="lg:hidden text-[hsl(var(--paper))]" onClick={() => setOpen(!open)}>
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {/* Mega menu */}
      <div
        onMouseEnter={() => setMega(true)} onMouseLeave={() => setMega(false)}
        className={`hidden lg:block absolute inset-x-0 top-[72px] bg-[hsl(var(--navy))] border-t border-white/10 shadow-2xl transition-all duration-300 origin-top ${mega ? "opacity-100 visible translate-y-0" : "opacity-0 invisible -translate-y-2"}`}
      >
        <div className="max-w-[1400px] mx-auto px-10 py-8 grid grid-cols-4 gap-x-8 gap-y-3">
          {cats.map((c) => (
            <Link key={c.slug} to={`/products/${c.slug}`} data-testid={`mega-${c.slug}`}
              className="group flex items-baseline justify-between py-1.5 border-b border-white/5">
              <span className="text-[hsl(var(--paper))]/85 group-hover:text-[hsl(var(--brass-2))] transition-colors text-sm">{c.name}</span>
              <span className="font-mono-l text-[10px] text-white/30">{String(c.product_count).padStart(2, "0")}</span>
            </Link>
          ))}
        </div>
      </div>

      {/* Mobile */}
      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-[hsl(var(--navy))] border-t border-white/10 max-h-[80vh] overflow-y-auto">
          <div className="px-5 py-4 flex flex-col">
            <NavLink to="/" className="py-2.5 text-[hsl(var(--paper))] border-b border-white/5">Home</NavLink>
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} className="py-2.5 text-[hsl(var(--paper))] border-b border-white/5">{n.label}</NavLink>
            ))}
            <NavLink to="/faq" className="py-2.5 text-[hsl(var(--paper))] border-b border-white/5">FAQ</NavLink>
            <NavLink to="/contact" className="py-2.5 text-[hsl(var(--brass-2))] font-semibold">Request a Quote</NavLink>
          </div>
        </div>
      )}
    </header>
  );
}

function Footer() {
  const [cats, setCats] = useState([]);
  useEffect(() => { getCategories().then(setCats).catch(() => {}); }, []);
  return (
    <footer data-testid="site-footer" className="bg-[hsl(var(--ink))] text-[hsl(var(--paper))]/80 relative grain">
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 pt-20 pb-10 relative">
        <div className="grid lg:grid-cols-12 gap-10 pb-14 border-b border-white/10">
          <div className="lg:col-span-4">
            <div className="font-display text-3xl text-[hsl(var(--paper))]">Ingren Trading LLP</div>
            <p className="mt-4 max-w-sm text-sm leading-relaxed">International B2B trading & supply. Ingren Trading LLP connects international buyers with vetted suppliers across a diverse range of commodities and industrial products.</p>
            <div className="mt-6 space-y-2 text-sm">
              <a href={`mailto:${COMPANY.email}`} className="flex items-center gap-2 hover:text-[hsl(var(--brass-2))]"><Mail size={15} /> {COMPANY.email}</a>
              <a href={`tel:${COMPANY.phone}`} className="flex items-center gap-2 hover:text-[hsl(var(--brass-2))]"><Phone size={15} /> {COMPANY.phone}</a>
              <a href={whatsappLink()} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-[hsl(var(--brass-2))]"><MessageCircle size={15} /> WhatsApp us</a>
            </div>
          </div>
          <div className="lg:col-span-4">
            <div className="eyebrow text-[hsl(var(--brass-2))] mb-4">Product Categories</div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
              {cats.slice(0, 12).map((c) => (
                <Link key={c.slug} to={`/products/${c.slug}`} className="hover:text-[hsl(var(--brass-2))] link-underline w-fit">{c.name}</Link>
              ))}
            </div>
          </div>
          <div className="lg:col-span-4">
            <div className="eyebrow text-[hsl(var(--brass-2))] mb-4">Company</div>
            <div className="flex flex-col gap-1.5 text-sm mb-6">
              {["About","Industries","Global-Shipping","Quality-Control","Certifications","FAQ","Blog","Contact"].map((l) => (
                <Link key={l} to={`/${l.toLowerCase()}`} className="hover:text-[hsl(var(--brass-2))] w-fit link-underline">{l.replace("-"," ")}</Link>
              ))}
            </div>
            <Newsletter />
          </div>
        </div>
        <div className="pt-8 flex flex-col md:flex-row justify-between gap-3 text-xs text-white/50">
          <div>© {new Date().getFullYear()} Ingren Trading LLP. All rights reserved.</div>
          <div className="font-mono-l">{COMPANY.email} · WhatsApp {COMPANY.phone}</div>
        </div>
      </div>
    </footer>
  );
}

export default function Layout() {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer />
      <a
        href={whatsappLink()} target="_blank" rel="noreferrer" data-testid="whatsapp-float"
        className="fixed bottom-6 right-6 z-50 h-14 w-14 rounded-full bg-[#25D366] text-white flex items-center justify-center shadow-2xl hover:scale-110 transition-transform"
        aria-label="Chat on WhatsApp"
      >
        <MessageCircle />
      </a>
    </div>
  );
}
